/**
 * WorkStagePanels — the production-stage stepper builder and its two detail
 * panels (per-stage milestone/event/operation timeline, delivery tracking).
 *
 * Extracted from `WorkUnitDetailPage.tsx`, which was retired once the merged
 * job workspace page (`../../../../pages/JobWorkspacePage.tsx`, keyed on
 * Tharstern job_no) took over `/jobs/:jobNo` — see
 * docs/features/estimate-job-transformation/. `WorkUnitDetailPage` used
 * these to render its sticky stage stepper and the active stage/delivery
 * panel; the merged page's Production and Delivery tabs reuse them
 * unchanged rather than duplicating the stage/row rendering logic.
 */

import { AlertTriangle, Check, Circle, CircleDot, Play } from "lucide-react";
import type * as API from "@shared-core/data/api-types";
import type {
  TrackingEmptyReason,
  TrackingResult,
} from "@shared-core/data/api/tracking-types";
import { isOverdue } from "@shared-core/domain/deadline";
import { formatTimeOrDash } from "@shared-core/domain/formatters";
import { TrackingResultCard } from "../delivery/TrackingResultCard";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../primitives/card";
import { type StepperStep } from "../../patterns/Stepper";
import { currentTime } from "@shared-core/domain/current-time";

// Stages come from the real backend response. The mock-data types are
// retained for stories only (see mock-data/work-visibility.ts).
type WorkStage = API.WorkStage;
type WorkStageEvent = API.WorkStageEvent;
type WorkStageMilestone = API.WorkStageMilestone;
type WorkOperation = API.WorkStageOperation;

// Milestone codes that just mirror an operation's start/finish — dropped
// from the panel because the operation row right next to them tells the
// same story. CSR/customer-facing gate milestones (Paper Ordered,
// Passed for Press, Despatched, Invoiced, etc.) stay visible.
const OP_MIRRORING_MILESTONE_CODES = new Set([700, 800, 900, 1000]);

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = then - currentTime().getTime();
  const abs = Math.abs(diff);
  const sec = Math.round(abs / 1000);
  if (sec < 60) return diff < 0 ? "just now" : "in <1m";
  const min = Math.round(sec / 60);
  if (min < 60) return diff < 0 ? `${min}m ago` : `in ${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return diff < 0 ? `${hr}h ago` : `in ${hr}h`;
  const d = Math.round(hr / 24);
  return diff < 0 ? `${d}d ago` : `in ${d}d`;
}

function formatHours(h: number): string {
  return `${h.toFixed(1)}h`;
}

// ---------------------------------------------------------------------------
// Stepper construction
// ---------------------------------------------------------------------------

export function stageSteps(stages: WorkStage[]): StepperStep[] {
  return stages.map((p) => ({
    id: p.op_type,
    label: p.display_name,
    state: p.state,
    // Triangle wins for amber/red — same lexicon as the row icons inside
    // the stage panel, so the ribbon and the panel narrate the same story.
    severity: p.severity,
  }));
}

// ---------------------------------------------------------------------------
// Delivery panel — shown when the user selects the Delivery step.
// Always rendered; shows an empty state early in the job lifecycle before
// any consignment is despatched.
// ---------------------------------------------------------------------------

// A courier lookup coming back empty isn't one thing — a job that hasn't
// shipped yet reads very differently from a courier CIS doesn't track,
// which reads differently again from a Tharstern/courier outage. Rendering
// all four as the same blank panel is the bug this map fixes (see
// delivery_tracking_module's lookup actions, which diagnose the reason
// server-side). `source_unavailable` and `courier_error` are honest
// failures — real problems worth surfacing differently from a merely empty
// state — even though the request itself returned 200.
const TRACKING_EMPTY_COPY: Record<TrackingEmptyReason, string> = {
  no_delivery_notes: "No delivery notes on this job yet.",
  not_despatched: "Not despatched yet — no consignment raised.",
  courier_not_tracked: "One doesn't track deliveries by this courier.",
  source_unavailable:
    "Couldn't reach Tharstern for tracking. Try again shortly.",
  courier_error: "Couldn't reach the courier for tracking. Try again shortly.",
};

const TRACKING_EMPTY_IS_FAILURE: Record<TrackingEmptyReason, boolean> = {
  no_delivery_notes: false,
  not_despatched: false,
  courier_not_tracked: false,
  source_unavailable: true,
  courier_error: true,
};

export function DeliveryPanel({
  tracking,
  emptyReason,
  untrackedCourier,
  trackingLoading,
  trackingFailed,
}: {
  tracking: TrackingResult[];
  emptyReason: TrackingEmptyReason | null;
  untrackedCourier: string | null;
  trackingLoading: boolean;
  trackingFailed: boolean;
}) {
  if (trackingLoading) {
    return (
      <Card>
        <CardContent className="pt-4 text-sm text-muted-foreground">
          Loading delivery information…
        </CardContent>
      </Card>
    );
  }
  if (trackingFailed) {
    return (
      <Card className="border-amber-300 bg-amber-50">
        <CardContent className="pt-4 text-sm text-amber-800">
          Delivery information could not be loaded. Please try refreshing the
          page.
        </CardContent>
      </Card>
    );
  }
  if (tracking.length === 0) {
    const isFailure =
      emptyReason != null && TRACKING_EMPTY_IS_FAILURE[emptyReason];
    const message = emptyReason
      ? emptyReason === "courier_not_tracked" && untrackedCourier
        ? `One doesn't track deliveries by ${untrackedCourier}.`
        : TRACKING_EMPTY_COPY[emptyReason]
      : "No delivery information yet. Consignment details will appear here once the job is despatched.";
    return (
      <Card className={isFailure ? "border-amber-300 bg-amber-50" : undefined}>
        <CardContent
          className={
            isFailure
              ? "pt-4 text-sm text-amber-800"
              : "pt-4 text-sm text-muted-foreground italic"
          }
        >
          {message}
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-3">
      {tracking.map((result) => (
        <TrackingResultCard
          key={`${result.courier_code}-${result.tracking_reference}`}
          result={result}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage panel — simplified single-list IA
// ---------------------------------------------------------------------------

// One row in the merged stage list — milestone, event, or operation —
// each carries a sort-time so the rendered list reads as a chronological
// story across all three sources.
type StageItem =
  | { kind: "milestone"; ts: number; key: string; node: WorkStageMilestone }
  | { kind: "event"; ts: number; key: string; node: WorkStageEvent }
  | { kind: "operation"; ts: number; key: string; node: WorkOperation };

function buildStageItems(stage: WorkStage): StageItem[] {
  // Two-bucket sort: things that have actually occurred / are occurring go
  // first in chronological order, then things still pending (whose
  // required/planned date is just an expectation) appear at the end. This
  // stops late pending items being interleaved with things that happened
  // around the same expected time — the screen reads as "here's what
  // happened, and here's what's still outstanding".
  const occurred: StageItem[] = [];
  const pending: StageItem[] = [];

  for (const m of stage.milestones ?? []) {
    if (OP_MIRRORING_MILESTONE_CODES.has(m.code)) continue;
    if (m.state === "complete") {
      occurred.push({
        kind: "milestone",
        ts: new Date(m.completed_at ?? 0).getTime(),
        key: `m-${m.code}-${m.paper_part_name ?? ""}`,
        node: m,
      });
    } else {
      pending.push({
        kind: "milestone",
        ts: new Date(m.required_at ?? 8.64e15).getTime(),
        key: `m-${m.code}-${m.paper_part_name ?? ""}`,
        node: m,
      });
    }
  }

  // Events are recorded after-the-fact — they always belong in the occurred
  // bucket. A "DPD courier booked for Thursday AM" event happened *now*
  // even though the booking is for the future.
  for (const e of stage.events ?? []) {
    occurred.push({
      kind: "event",
      ts: new Date(e.occurred_at).getTime(),
      key: `e-${e.id}`,
      node: e,
    });
  }

  for (const o of stage.operations ?? []) {
    if (o.state === "complete" || o.state === "in_progress") {
      occurred.push({
        kind: "operation",
        ts: new Date(o.actual_started_at ?? 0).getTime(),
        key: `o-${o.id}`,
        node: o,
      });
    } else {
      pending.push({
        kind: "operation",
        ts: new Date(o.planned_start_at ?? 8.64e15).getTime(),
        key: `o-${o.id}`,
        node: o,
      });
    }
  }

  occurred.sort((a, b) => a.ts - b.ts);
  pending.sort((a, b) => a.ts - b.ts);
  return [...occurred, ...pending];
}

// Pending milestone is overdue once its required_at DATE has passed —
// `required_at` comes from a Postgres `date` column (Tharstern's
// required_date), so it parses to local midnight and a naive time-of-day
// comparison would flag a milestone required today as overdue from 00:00.
// `isOverdue` compares calendar days, matching the job-level due-date rule.
export function milestoneRowState(milestone: WorkStageMilestone): RowState {
  if (milestone.state === "complete") return "complete";
  if (milestone.required_at && isOverdue(milestone.required_at)) {
    return "overdue";
  }
  return "pending";
}

// Planned operation is overdue when its planned_end_at (or planned_start_at
// if no end) is in the past — i.e. it should have happened by now.
function operationRowState(op: WorkOperation): RowState {
  if (op.state === "in_progress") return "in_progress";
  if (op.state === "complete") return "complete";
  const due = op.planned_end_at ?? op.planned_start_at;
  if (due && new Date(due).getTime() < currentTime().getTime())
    return "overdue";
  return "planned";
}

export function StagePanel({ stage }: { stage: WorkStage }) {
  const items = buildStageItems(stage);

  const stateLabel =
    stage.state === "in_progress"
      ? "in progress"
      : stage.state === "complete"
        ? "complete"
        : "pending";

  // Summary phrase only earns its keep on in-progress stages — that's
  // where the rows below are mid-flow and a CSR-friendly one-liner adds
  // signal. Complete and pending stages are self-evident from the rows.
  const showSummary = stage.state === "in_progress";

  // Title colouring follows severity for in-progress/pending, but stays
  // neutral for complete (a done stage isn't "red" even if it had drama).
  const titleClass =
    stage.state === "complete"
      ? "text-foreground"
      : stage.severity === "red"
        ? "text-red-700"
        : stage.severity === "amber"
          ? "text-amber-700"
          : "text-foreground";

  return (
    <Card>
      <CardHeader className="space-y-2">
        <CardTitle className={`text-base ${titleClass}`}>
          <StageStateIcon state={stage.state} severity={stage.severity} />
          <span className="ml-2">
            {stage.display_name} — {stateLabel}
          </span>
        </CardTitle>
        {showSummary && stage.summary_phrase && (
          <p
            className={`text-sm ${
              stage.severity === "red"
                ? "text-red-700"
                : stage.severity === "amber"
                  ? "text-amber-700"
                  : "text-muted-foreground"
            }`}
          >
            {stage.summary_phrase}
          </p>
        )}
        {(stage.hours_posted ?? 0) > 0 && (
          <p className="text-xs text-muted-foreground">
            {formatHours(stage.hours_posted)} posted
            {stage.operators && stage.operators.length > 0
              ? ` · ${stage.operators.map((o) => o.name).join(", ")}`
              : ""}
          </p>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            Nothing recorded yet.
          </p>
        ) : (
          <ul className="divide-y divide-border/60">
            {items.map((item) => (
              <li key={item.key} className="py-2">
                {item.kind === "milestone" && (
                  <MilestoneRow milestone={item.node} />
                )}
                {item.kind === "event" && <EventRow event={item.node} />}
                {item.kind === "operation" && (
                  <OperationRow operation={item.node} />
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function StageStateIcon({
  state,
  severity,
}: {
  state: WorkStage["state"];
  severity: WorkStage["severity"];
}) {
  if (state === "complete") {
    return (
      <Check className="inline size-4 text-emerald-600" aria-label="complete" />
    );
  }
  if (state === "in_progress") {
    const colour =
      severity === "red"
        ? "text-red-600"
        : severity === "amber"
          ? "text-amber-600"
          : "text-emerald-600";
    return (
      <Play className={`inline size-4 ${colour}`} aria-label="in progress" />
    );
  }
  return (
    <Circle
      className="inline size-4 text-muted-foreground"
      aria-label="pending"
    />
  );
}

// ---------------------------------------------------------------------------
// Row leading-icon and kind badge. The state icon gives the at-a-glance
// "how is this going" signal; the kind badge names which of the three
// underlying Tharstern data sources this row comes from (milestone =
// gate-checkpoint flag, event = customer/supplier action, operation =
// literal machine work). The three types behave very differently — the
// badge makes that distinction explicit instead of leaving the reader
// to infer it from row content.
// ---------------------------------------------------------------------------

export type RowState =
  "complete" | "in_progress" | "pending" | "planned" | "overdue";
type RowKind = "milestone" | "event" | "operation";

function RowStateIcon({ state }: { state: RowState }) {
  // mt-0.5 aligns the icon roughly with the first text baseline of the
  // adjacent columns under the row's items-start layout.
  const common = "size-3.5 shrink-0 mt-0.5";
  if (state === "complete") {
    return <Check className={`${common} text-emerald-600`} />;
  }
  if (state === "in_progress") {
    return <Play className={`${common} text-emerald-600`} />;
  }
  if (state === "overdue") {
    // AlertTriangle echoes the page-level alert chip — the visual link is
    // deliberate. A red row means "this is part of why the alert fires".
    return <AlertTriangle className={`${common} text-red-600`} />;
  }
  if (state === "planned") {
    return <CircleDot className={`${common} text-muted-foreground/70`} />;
  }
  return <Circle className={`${common} text-muted-foreground`} />;
}

const KIND_BADGE_CLASS: Record<RowKind, string> = {
  // Three subtle colour-coded pills — distinct enough to scan, quiet
  // enough not to compete with the row content. Hues chosen to sit
  // outside the state-icon palette (emerald=done, primary=running,
  // amber/red=overdue) so the eye never confuses kind with state.
  milestone: "bg-slate-100 text-slate-600 ring-slate-200",
  event: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  operation: "bg-teal-50 text-teal-700 ring-teal-200",
};

const KIND_BADGE_LABEL: Record<RowKind, string> = {
  milestone: "Milestone",
  event: "Event",
  operation: "Operation",
};

function KindBadge({ kind }: { kind: RowKind }) {
  // mt-0.5 keeps the badge aligned with the first text line under the
  // shared items-start row layout.
  return (
    <span
      className={`shrink-0 inline-flex items-center px-1.5 py-px rounded text-[10px] font-medium uppercase tracking-wide ring-1 mt-0.5 ${KIND_BADGE_CLASS[kind]}`}
    >
      {KIND_BADGE_LABEL[kind]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// People resolution — backend now returns full names directly via
// SQL-view JOINs (JobMilestones.UserID → Users.FullName; Events
// already carries *UserName columns; TimeSheetEntries.OperatorName).
// resolvePersonName is now a thin identity-coerce that strips empty
// strings.
// ---------------------------------------------------------------------------

function resolvePersonName(value: string | null | undefined): string | null {
  if (!value) return null;
  return value;
}

// ---------------------------------------------------------------------------
// Universal row anatomy. All three row types (milestone / event / operation)
// render as four left-justified columns aligned across the entire stage
// panel so the eye can scan a column for any concept:
//
//   [tick] [content: title + time + (operation metrics)] [person] [kind badge]
//
// Implemented with a flex shell + a flex-1 content col and fixed widths on
// the other three. Everything top-aligned so multi-line content cascades
// downward from a common baseline.
// ---------------------------------------------------------------------------

const ROW_GRID = "flex items-start gap-6 text-sm";
const ROW_COL_CONTENT = "flex-1 min-w-0 leading-tight";
const ROW_COL_PERSON = "w-32 shrink-0 leading-tight text-xs";
// Fixed-width badge wrapper so every row's badge starts at the same x.
// Without this, MILESTONE/EVENT/OPERATION badges' different intrinsic
// widths shifted the person column left/right, breaking column alignment.
const ROW_COL_BADGE = "w-24 shrink-0";

function PersonCol({ person }: { person: string | null }) {
  return (
    <div className={ROW_COL_PERSON}>
      {person ? (
        <span className="text-foreground">{person}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Milestone row — one line, gate-event only
// ---------------------------------------------------------------------------

// Universal title-colour rule applied across all three row types:
//   complete / in_progress → foreground
//   overdue                → red (matches alert chip palette)
//   pending / planned      → muted
function titleClassForState(state: RowState): string {
  if (state === "overdue") return "text-red-700 font-medium";
  if (state === "complete" || state === "in_progress") return "text-foreground";
  return "text-muted-foreground";
}

function subtextClassForState(state: RowState): string {
  return state === "overdue" ? "text-red-600" : "text-muted-foreground";
}

function MilestoneRow({ milestone }: { milestone: WorkStageMilestone }) {
  const rowState = milestoneRowState(milestone);
  const done = rowState === "complete";
  const person = done ? resolvePersonName(milestone.completed_by) : null;
  const when = done
    ? relativeTime(milestone.completed_at)
    : milestone.required_at
      ? `expected ${relativeTime(milestone.required_at)}`
      : null;

  return (
    <div className={ROW_GRID}>
      <RowStateIcon state={rowState} />
      <div className={ROW_COL_CONTENT}>
        <div className={titleClassForState(rowState)}>
          {milestone.name}
          {milestone.paper_part_name && (
            <span className="ml-1.5 text-xs text-muted-foreground">
              [{milestone.paper_part_name}]
            </span>
          )}
        </div>
        {when && (
          <div className={`text-xs mt-0.5 ${subtextClassForState(rowState)}`}>
            {when}
          </div>
        )}
      </div>
      <PersonCol person={person} />
      <div className={ROW_COL_BADGE}>
        <KindBadge kind="milestone" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Event row — one line, non-milestone non-operation events (proofs, outwork,
// deliveries, general). Soft visual weight so they sit alongside but don't
// compete with operation rows.
// ---------------------------------------------------------------------------

function EventRow({ event }: { event: WorkStageEvent }) {
  // Events are records of things that happened — treat them as "complete"
  // for state-icon purposes. A future scheduled event (e.g. a booking for
  // tomorrow) is still a *recorded* event, not a pending state.
  // "Who" prefers the internal initiator (it's a person we can name); when
  // there's none (a customer-driven proof approval, say) fall back to the
  // external party so the right column still names an actor.
  const person = resolvePersonName(event.initiated_by) ?? event.party ?? null;
  const when = relativeTime(event.occurred_at);

  return (
    <div className={ROW_GRID}>
      <RowStateIcon state="complete" />
      <div className={ROW_COL_CONTENT}>
        <div className={titleClassForState("complete")}>
          {event.display_text}
          {event.party && event.initiated_by && (
            <span className="text-muted-foreground"> · {event.party}</span>
          )}
        </div>
        {when && (
          <div className={`text-xs mt-0.5 ${subtextClassForState("complete")}`}>
            {when}
          </div>
        )}
      </div>
      <PersonCol person={person} />
      <div className={ROW_COL_BADGE}>
        <KindBadge kind="event" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Operation row — 2 lines default, click to expand for planned/off-est detail
// ---------------------------------------------------------------------------

function OperationRow({ operation }: { operation: WorkOperation }) {
  // operationRowState collapses to the unified RowState and elevates
  // an unstarted planned op to "overdue" if its planned end is in the
  // past — same treatment as a milestone whose required-by date has passed.
  const rowState = operationRowState(operation);

  // Operators and posted hours are a stage-level attribute (timesheet
  // entries link to a process type, not a specific operation row), so the
  // per-operation person column is intentionally blank — the stage header
  // carries posted hours + operators for the stage.
  const person = null;

  // Time fragment that opens the subline.
  const whenFragment = (() => {
    if (operation.state === "in_progress" && operation.actual_started_at) {
      return `running since ${formatTimeOrDash(operation.actual_started_at)}`;
    }
    if (operation.state === "complete" && operation.actual_finished_at) {
      return `finished ${relativeTime(operation.actual_finished_at)}`;
    }
    if (operation.planned_start_at) {
      return rowState === "overdue"
        ? `was due ${relativeTime(operation.planned_start_at)}`
        : `scheduled ${formatTimeOrDash(operation.planned_start_at)}`;
    }
    return null;
  })();

  // Operation-specific metric fragments (hours posted, qty produced, etc).
  // Combined with the time fragment above into a single bullet-separated
  // subline so the row stays one extra row tall, not two.
  const metricFragments: string[] = (() => {
    if (operation.state === "in_progress") {
      const parts: string[] = [];
      if (operation.qty_required != null) {
        parts.push(
          `${(operation.qty_produced ?? 0).toLocaleString()} of ${operation.qty_required.toLocaleString()}`,
        );
      }
      return parts;
    }
    if (operation.state === "complete") {
      const parts: string[] = [];
      if (operation.qty_produced != null) {
        parts.push(`${operation.qty_produced.toLocaleString()} produced`);
      }
      return parts;
    }
    return operation.hours_estimated != null
      ? [`est. ${formatHours(operation.hours_estimated)}`]
      : [];
  })();

  const subline = [whenFragment, ...metricFragments]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className={ROW_GRID}>
      <RowStateIcon state={rowState} />
      <div className={ROW_COL_CONTENT}>
        <div className={titleClassForState(rowState)}>
          {operation.machine_name}
          <span className="text-muted-foreground">
            {" · "}
            {operation.operation_name}
          </span>
          {operation.paper_part_name && (
            <span className="ml-1.5 text-xs text-muted-foreground">
              [{operation.paper_part_name}]
            </span>
          )}
        </div>
        {subline && (
          <div className={`text-xs mt-0.5 ${subtextClassForState(rowState)}`}>
            {subline}
          </div>
        )}
      </div>
      <PersonCol person={person} />
      <div className={ROW_COL_BADGE}>
        <KindBadge kind="operation" />
      </div>
    </div>
  );
}
